import React, { useState, useEffect, useCallback } from 'react';
import { TransitionService } from '../../services/api/transitionService';
import { ArtifactWithParameters } from '../../types/artifacts';
import { ArtifactItemState } from '../../types/artifactItems';
import { API_BASE_URL } from '../../config';
import styles from './AddArtifactDialog.module.css';

interface AddArtifactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  processId: string;
  processName: string;
  artifactRef: string; // The main artifact this process belongs to
  artifactTreeId: string; // The artifact tree ID to filter artifacts
}

interface ProcessRole {
  id: string;
  name: string;
  description: string;
}

const PROCESS_ROLES: ProcessRole[] = [
  {
    id: 'PROCESSITEM',
    name: 'Process Item',
    description:
      'The artifact changes state in a way that transforms the artifact. Example: a bike artifact goes from a production order state to an assembled state.',
  },
  {
    id: 'PROCESSITEM_PERSISTENT',
    name: 'Process Item (Persistent)',
    description:
      'The artifact changes state but the previous state persists. Example: a prospective customer orders a product, they still are a prospect for additional purchases and they are a customer that has purchased an item.',
  },
  {
    id: 'MATERIAL',
    name: 'Material',
    description:
      'The artifact is consumed in the process. The transition moves the artifact to its final state.',
  },
  {
    id: 'CREATION',
    name: 'Created',
    description:
      'The artifact items initial state is in the output of the process, no input state for the transition.',
  },
  {
    id: 'RESOURCE',
    name: 'Resource',
    description:
      'The artifact provides resources for the process but is not transformed or consumed.',
  },
];

export const AddArtifactDialog: React.FC<AddArtifactDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  processId,
  processName,
  artifactTreeId,
}) => {
  const [step, setStep] = useState(1);
  const [selectedArtifact, setSelectedArtifact] = useState<ArtifactWithParameters | null>(null);
  const [availableArtifacts, setAvailableArtifacts] = useState<ArtifactWithParameters[]>([]);
  const [artifactStates, setArtifactStates] = useState<ArtifactItemState[]>([]);
  const [selectedInputState, setSelectedInputState] = useState<ArtifactItemState | null>(null);
  const [selectedOutputState, setSelectedOutputState] = useState<ArtifactItemState | null>(null);
  const [selectedProcessRole, setSelectedProcessRole] = useState<ProcessRole | null>(null);
  const [transitionName, setTransitionName] = useState('');
  const [transitionDescription, setTransitionDescription] = useState('');
  const [inputOutputRatio, setInputOutputRatio] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateArtifact, setShowCreateArtifact] = useState(false);
  const [newArtifactName, setNewArtifactName] = useState('');
  const [newArtifactDescription, setNewArtifactDescription] = useState('');
  const [selectedParentArtifact, setSelectedParentArtifact] =
    useState<ArtifactWithParameters | null>(null);
  const [showCreateInputState, setShowCreateInputState] = useState(false);
  const [showCreateOutputState, setShowCreateOutputState] = useState(false);
  const [newStateName, setNewStateName] = useState('');
  const [newStateDescription, setNewStateDescription] = useState('');
  const [newStateType, setNewStateType] = useState('PROCESSITEM');

  const buildApiUrl = (path: string) => `${API_BASE_URL}${path}`;

  const loadAvailableArtifacts = useCallback(
    async (searchTerm: string = '') => {
      try {
        setLoading(true);
        // Get artifacts from the specific tree
        const response = await fetch(buildApiUrl(`/artifacts/tree/${artifactTreeId}?limit=100`));
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const artifacts = await response.json();

        // Filter artifacts based on search term
        const filteredArtifacts = searchTerm
          ? artifacts.filter(
              (artifact: ArtifactWithParameters) =>
                artifact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                artifact.description?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          : artifacts;

        setAvailableArtifacts(filteredArtifacts);
        setError(null);
      } catch (err) {
        console.error('Error loading artifacts:', err);
        setError('Failed to load artifacts from the database');
        setAvailableArtifacts([]);
      } finally {
        setLoading(false);
      }
    },
    [artifactTreeId]
  );

  useEffect(() => {
    if (isOpen) {
      void loadAvailableArtifacts();
    } else {
      // Reset form when dialog closes
      setStep(1);
      setSelectedArtifact(null);
      setArtifactStates([]);
      setSelectedInputState(null);
      setSelectedOutputState(null);
      setSelectedProcessRole(null);
      setTransitionName('');
      setTransitionDescription('');
      setInputOutputRatio(1.0);
      setError(null);
    }
  }, [isOpen, loadAvailableArtifacts]);

  const handleArtifactSelect = async (artifact: ArtifactWithParameters) => {
    setSelectedArtifact(artifact);
    setError(null);

    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/artifacts/${artifact.id}/states`));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const states = await response.json();
      setArtifactStates(states);
      setStep(2);
    } catch (err) {
      console.error('Error loading artifact states:', err);
      setError('Failed to load artifact states');
      setArtifactStates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewArtifact = async () => {
    if (!newArtifactName.trim()) {
      setError('Please enter an artifact name');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const newArtifactData = {
        name: newArtifactName.trim(),
        description: newArtifactDescription.trim() || null,
        artifact_type_id: '971af543-798f-4901-8cfa-b47cfd021646', // Default category type
        parent_ref: selectedParentArtifact?.id || null,
        artifact_tree_id: artifactTreeId, // Use the current artifact tree ID
        business_model_id: '6a866fdf-d826-4926-a4bb-aa228909c06b', // Default business model ID
      };

      const response = await fetch(buildApiUrl('/artifacts'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newArtifactData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newArtifact = await response.json();

      // Add the new artifact to the list and select it
      setAvailableArtifacts((prev) => [newArtifact, ...prev]);
      setSelectedArtifact(newArtifact);

      // Reset create artifact form
      setShowCreateArtifact(false);
      setNewArtifactName('');
      setNewArtifactDescription('');
      setSelectedParentArtifact(null);

      // Move to step 2
      void handleArtifactSelect(newArtifact);
    } catch (err) {
      console.error('Error creating artifact:', err);
      setError('Failed to create new artifact');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewState = async (stateType: 'input' | 'output') => {
    if (!newStateName.trim()) {
      setError('Please enter a state name');
      return;
    }

    if (!selectedArtifact) {
      setError('No artifact selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const newStateData = {
        name: newStateName.trim(),
        description: newStateDescription.trim() || null,
        artifact_ref: selectedArtifact.id,
        artifact_id: selectedArtifact.id,
        is_required: true,
        state_type: newStateType,
        is_initial: false,
        is_final: false,
        is_improvement: false,
        demand_type: 'TARGET',
        demand_flow_type: 'DRUM',
        state_metadata: {},
        state_rules: {},
        state_parameters: {},
        validation_rules: {},
      };

      const response = await fetch(buildApiUrl(`/artifacts/${selectedArtifact.id}/states`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newStateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newState = await response.json();

      // Add the new state to the list
      setArtifactStates((prev) => [...prev, newState]);

      // Select the new state based on type
      if (stateType === 'input') {
        setSelectedInputState(newState);
        setShowCreateInputState(false);
      } else {
        setSelectedOutputState(newState);
        setShowCreateOutputState(false);
      }

      // Reset form
      setNewStateName('');
      setNewStateDescription('');
      setNewStateType('PROCESSITEM');
    } catch (err) {
      console.error('Error creating state:', err);
      setError('Failed to create new state');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransition = async () => {
    if (!selectedArtifact || !selectedOutputState || !selectedProcessRole) {
      setError('Please fill in all required fields');
      return;
    }

    // For CREATION type, input state is not required
    if (selectedProcessRole.id !== 'CREATION' && !selectedInputState) {
      setError('Please select an input state');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const transitionData = {
        name: transitionName || `${processName} - ${selectedProcessRole.name}`,
        description: transitionDescription,
        artifact_id: selectedArtifact.id,
        artifact_process_ref: processId,
        transition_type: selectedProcessRole.id,
        from_state_id: selectedProcessRole.id === 'CREATION' ? '' : selectedInputState?.id || '',
        to_state_id: selectedOutputState.id,
        input_output_ratio: inputOutputRatio,
        is_active: true,
        demand_type: 'TARGET',
        demand_flow_type: 'DRUM',
        effective_start_date: new Date().toISOString(),
        effective_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
        created_at: new Date().toISOString(),
      };

      console.log('[AddArtifactDialog] Creating transition with data:', transitionData);
      console.log('[AddArtifactDialog] Process ID:', processId);
      console.log('[AddArtifactDialog] Selected Artifact ID:', selectedArtifact.id);
      console.log(
        '[AddArtifactDialog] artifact_process_ref in data:',
        'artifact_process_ref' in transitionData
      );
      console.log(
        '[AddArtifactDialog] artifact_process_ref value:',
        transitionData.artifact_process_ref
      );
      console.log('[AddArtifactDialog] Full transition data keys:', Object.keys(transitionData));
      console.log(
        '[AddArtifactDialog] Full transition data values:',
        Object.values(transitionData)
      );
      console.log(
        '[AddArtifactDialog] JSON.stringify(transitionData):',
        JSON.stringify(transitionData, null, 2)
      );

      await TransitionService.create(selectedArtifact.id, transitionData);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating transition:', err);
      setError('Failed to create transition');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.addArtifactDialog}>
        <div className={styles.dialogHeader}>
          <h2>Add Artifact to Process</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.dialogContent}>
          {error && <div className={styles.errorMessage}>{error}</div>}

          {step === 1 && (
            <div className={styles.stepContent}>
              <h3>Step 1: Select Artifact</h3>
              <p>Choose the artifact you want to add to this process.</p>

              <div className={styles.searchSection}>
                <div className={styles.searchControls}>
                  <input
                    type="text"
                    placeholder="Search artifacts..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      void loadAvailableArtifacts(e.target.value);
                    }}
                    className={styles.searchInput}
                  />
                  <button
                    className={`${styles.btn} ${styles.btnSecondary}`}
                    onClick={() => setShowCreateArtifact(!showCreateArtifact)}
                  >
                    {showCreateArtifact ? 'Cancel' : '+ Create New Artifact'}
                  </button>
                </div>
              </div>

              {showCreateArtifact && (
                <div className={styles.createArtifactForm}>
                  <h4>Create New Artifact</h4>
                  <div className={styles.formSection}>
                    <label>Artifact Name:</label>
                    <input
                      type="text"
                      value={newArtifactName}
                      onChange={(e) => setNewArtifactName(e.target.value)}
                      placeholder="Enter artifact name"
                    />
                  </div>
                  <div className={styles.formSection}>
                    <label>Description:</label>
                    <textarea
                      value={newArtifactDescription}
                      onChange={(e) => setNewArtifactDescription(e.target.value)}
                      placeholder="Enter artifact description (optional)"
                      rows={2}
                    />
                  </div>
                  <div className={styles.formSection}>
                    <label>Parent Artifact (optional):</label>
                    <select
                      value={selectedParentArtifact?.id || ''}
                      onChange={(e) => {
                        const parent = availableArtifacts.find((a) => a.id === e.target.value);
                        setSelectedParentArtifact(parent || null);
                      }}
                    >
                      <option value="">No parent (root level)</option>
                      {availableArtifacts.map((artifact) => (
                        <option key={artifact.id} value={artifact.id}>
                          {artifact.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.formActions}>
                    <button
                      className={`${styles.btn} ${styles.btnPrimary}`}
                      onClick={handleCreateNewArtifact}
                      disabled={loading || !newArtifactName.trim()}
                    >
                      {loading ? 'Creating...' : 'Create Artifact'}
                    </button>
                  </div>
                </div>
              )}

              {loading ? (
                <div className={styles.loading}>Loading artifacts...</div>
              ) : (
                <div className={styles.artifactsList}>
                  {availableArtifacts.length === 0 ? (
                    <div className={styles.noArtifacts}>
                      <p>No artifacts found.</p>
                      {searchTerm && (
                        <p>Try adjusting your search terms or create a new artifact.</p>
                      )}
                    </div>
                  ) : (
                    availableArtifacts.map((artifact) => (
                      <div
                        key={artifact.id}
                        className={`${styles.artifactItem} ${selectedArtifact?.id === artifact.id ? styles.selected : ''}`}
                        onClick={() => handleArtifactSelect(artifact)}
                      >
                        <div className={styles.artifactName}>{artifact.name}</div>
                        <div className={styles.artifactDescription}>
                          {artifact.description || 'No description'}
                        </div>
                        {artifact.parent_ref && (
                          <div className={styles.artifactParent}>
                            Parent:{' '}
                            {availableArtifacts.find((a) => a.id === artifact.parent_ref)?.name ||
                              'Unknown'}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className={styles.stepContent}>
              <h3>Step 2: Configure States and Role</h3>
              <p>
                Selected artifact: <strong>{selectedArtifact?.name}</strong>
              </p>

              <div className={styles.formSection}>
                <label>Process Role:</label>
                <div className={styles.processRoles}>
                  {PROCESS_ROLES.map((role) => (
                    <div
                      key={role.id}
                      className={`${styles.processRole} ${selectedProcessRole?.id === role.id ? styles.selected : ''}`}
                      onClick={() => setSelectedProcessRole(role)}
                    >
                      <div className={styles.roleName}>{role.name}</div>
                      <div className={styles.roleDescription}>{role.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedProcessRole?.id !== 'CREATION' ? (
                <div className={styles.formSection}>
                  <label>Input State:</label>
                  <select
                    value={selectedInputState?.id || ''}
                    onChange={(e) => {
                      if (e.target.value === 'CREATE_NEW') {
                        setShowCreateInputState(true);
                        setShowCreateOutputState(false);
                      } else {
                        const state = artifactStates.find((s) => s.id === e.target.value);
                        setSelectedInputState(state || null);
                      }
                    }}
                  >
                    <option value="">Select input state...</option>
                    {artifactStates.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name} ({state.state_type})
                      </option>
                    ))}
                    <option value="CREATE_NEW">+ Add new input state</option>
                  </select>

                  {showCreateInputState && (
                    <div className={styles.createStateForm}>
                      <h5>Create New Input State</h5>
                      <div className={styles.formSection}>
                        <label>State Name:</label>
                        <input
                          type="text"
                          value={newStateName}
                          onChange={(e) => setNewStateName(e.target.value)}
                          placeholder="Enter state name"
                        />
                      </div>
                      <div className={styles.formSection}>
                        <label>Description:</label>
                        <textarea
                          value={newStateDescription}
                          onChange={(e) => setNewStateDescription(e.target.value)}
                          placeholder="Enter state description (optional)"
                          rows={2}
                        />
                      </div>
                      <div className={styles.formSection}>
                        <label>State Type:</label>
                        <select
                          value={newStateType}
                          onChange={(e) => setNewStateType(e.target.value)}
                        >
                          <option value="PROCESSITEM">Process Item</option>
                          <option value="MATERIAL">Material</option>
                          <option value="RESOURCE">Resource</option>
                        </select>
                      </div>
                      <div className={styles.formActions}>
                        <button
                          className={`${styles.btn} ${styles.btnPrimary}`}
                          onClick={() => handleCreateNewState('input')}
                          disabled={loading || !newStateName.trim()}
                        >
                          {loading ? 'Creating...' : 'Create State'}
                        </button>
                        <button
                          className={`${styles.btn} ${styles.btnSecondary}`}
                          onClick={() => {
                            setShowCreateInputState(false);
                            setNewStateName('');
                            setNewStateDescription('');
                            setNewStateType('PROCESSITEM');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.formSection}>
                  <div className={styles.infoNote}>
                    <strong>Creation Type:</strong> This artifact will be created in the process, so
                    no input state is needed.
                  </div>
                </div>
              )}

              <div className={styles.formSection}>
                <label>Output State:</label>
                <select
                  value={selectedOutputState?.id || ''}
                  onChange={(e) => {
                    if (e.target.value === 'CREATE_NEW') {
                      setShowCreateOutputState(true);
                      setShowCreateInputState(false);
                    } else {
                      const state = artifactStates.find((s) => s.id === e.target.value);
                      setSelectedOutputState(state || null);
                    }
                  }}
                >
                  <option value="">Select output state...</option>
                  {artifactStates.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name} ({state.state_type})
                    </option>
                  ))}
                  <option value="CREATE_NEW">+ Add new output state</option>
                </select>

                {showCreateOutputState && (
                  <div className={styles.createStateForm}>
                    <h5>Create New Output State</h5>
                    <div className={styles.formSection}>
                      <label>State Name:</label>
                      <input
                        type="text"
                        value={newStateName}
                        onChange={(e) => setNewStateName(e.target.value)}
                        placeholder="Enter state name"
                      />
                    </div>
                    <div className={styles.formSection}>
                      <label>Description:</label>
                      <textarea
                        value={newStateDescription}
                        onChange={(e) => setNewStateDescription(e.target.value)}
                        placeholder="Enter state description (optional)"
                        rows={2}
                      />
                    </div>
                    <div className={styles.formSection}>
                      <label>State Type:</label>
                      <select
                        value={newStateType}
                        onChange={(e) => setNewStateType(e.target.value)}
                      >
                        <option value="PROCESSITEM">Process Item</option>
                        <option value="MATERIAL">Material</option>
                        <option value="RESOURCE">Resource</option>
                      </select>
                    </div>
                    <div className={styles.formActions}>
                      <button
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={() => handleCreateNewState('output')}
                        disabled={loading || !newStateName.trim()}
                      >
                        {loading ? 'Creating...' : 'Create State'}
                      </button>
                      <button
                        className={`${styles.btn} ${styles.btnSecondary}`}
                        onClick={() => {
                          setShowCreateOutputState(false);
                          setNewStateName('');
                          setNewStateDescription('');
                          setNewStateType('PROCESSITEM');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.formSection}>
                <label>Transition Name:</label>
                <input
                  type="text"
                  value={transitionName}
                  onChange={(e) => setTransitionName(e.target.value)}
                  placeholder={
                    selectedProcessRole
                      ? `Default: ${processName} - ${selectedProcessRole.name}`
                      : 'Enter transition name (optional)'
                  }
                />
              </div>

              <div className={styles.formSection}>
                <label>Transition Description:</label>
                <textarea
                  value={transitionDescription}
                  onChange={(e) => setTransitionDescription(e.target.value)}
                  placeholder="Enter transition description (optional)"
                  rows={3}
                />
              </div>

              <div className={styles.formSection}>
                <label>Input/Output Ratio:</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={inputOutputRatio}
                  onChange={(e) => setInputOutputRatio(parseFloat(e.target.value) || 1.0)}
                />
              </div>
            </div>
          )}
        </div>

        <div className={styles.dialogActions}>
          {step === 2 && (
            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setStep(1)}>
              ← Back
            </button>
          )}
          <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose}>
            Cancel
          </button>
          {step === 2 && (
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleCreateTransition}
              disabled={
                loading ||
                !selectedOutputState ||
                !selectedProcessRole ||
                (selectedProcessRole.id !== 'CREATION' && !selectedInputState)
              }
            >
              {loading ? 'Creating...' : 'Create Transition'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddArtifactDialog;
